import {
  QueryClient,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { CityData } from "./city";
import { z } from "zod";
import { MAX_5_CHARACTERS_MESSAGE, REQUIRED_MESSAGE } from "@/utils/constant";
import { axiosInstance } from "@/utils/lib/axios";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { Paginated } from "./types";
import { generateParams } from "@/utils/helper/generateParams";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { stringRange } from "@/utils/helper/refinement";
import { AxiosResponse } from "axios";

const subdistrictPayloadSchema = z.object({
  subdistrictCode: z
    .string()
    .max(5, { message: MAX_5_CHARACTERS_MESSAGE })
    .nullable()
    .optional(),
  subdistrictName: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(4, 50)),
  city: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
});

type SubdistrictPayloadSchema = z.infer<typeof subdistrictPayloadSchema>;

type SubdistrictData = {
  id: string;
  subdistrictCode?: string | null;
  subdistrictName: string;
  city: CityData;
};

/* ================ *
 * READ subdistrict *
 * ================ */

type SubdistrictsQueryKey = DataConfig & {
  subdistrictName?: string;
};
interface SubdistrictList extends Paginated {
  content: SubdistrictData[];
}
async function getSubdistricts(
  queryFunctionContext: QueryFunctionContext,
): Promise<SubdistrictList> {
  const { queryKey } = queryFunctionContext;
  const [_, subdistrictsQueryKey] = queryKey;
  const params = generateParams(subdistrictsQueryKey as SubdistrictsQueryKey);

  const response = await axiosInstance.get(`/api/crud/subdistrict${params}`);
  return response.data.data;
}

function useSubdistricts(
  subdistrictsQueryKey: SubdistrictsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["subdistricts", subdistrictsQueryKey],
    queryFn: getSubdistricts,
  });
}

interface SubdistrictQueryKey {
  id: string;
}

async function getSubdistrict(
  queryFunctionContext: QueryFunctionContext,
): Promise<SubdistrictData> {
  const { queryKey } = queryFunctionContext;
  const [_, subdistrictQueryKey] = queryKey;
  const { id } = subdistrictQueryKey as SubdistrictQueryKey;

  const response = await axiosInstance.get(`/api/crud/subdistrict/${id}`);
  return response.data.data;
}

function useSubdistrict(subdistrictQueryKey: SubdistrictQueryKey) {
  const { id } = subdistrictQueryKey;
  return useQuery({
    queryKey: ["subdistricts", { id }],
    queryFn: getSubdistrict,
  });
}

function subdistrictLoader(
  queryClient: QueryClient,
): LoaderFunction<SubdistrictData> {
  return async ({ params }: LoaderFunctionArgs<{ id: string }>) => {
    const subdistrict = await queryClient.ensureQueryData({
      queryKey: ["subdistricts", { id: params.id }],
      queryFn: getSubdistrict,
    });
    return subdistrict;
  };
}

/* ================== *
 * CREATE subdistrict *
 * ================== */

async function createSubdistrict(
  data: SubdistrictPayloadSchema,
): Promise<SubdistrictData> {
  const response = await axiosInstance.post("/api/crud/subdistrict", data);
  return response.data;
}

function useSubdistrictCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: SubdistrictPayloadSchema }) =>
      createSubdistrict(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subdistricts"],
      });
    },
  });
}

/* ================== *
 * UPDATE subdistrict *
 * ================== */

async function editSubdistrict(id: string, data: SubdistrictPayloadSchema) {
  const response = await axiosInstance.post(
    `/api/crud/subdistrict/${id}`,
    data,
  );
  return response.data;
}

function useSubdistrictEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: SubdistrictPayloadSchema;
    }) => editSubdistrict(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subdistricts"],
      });
    },
  });
}

/* ================== *
 * DELETE subdistrict *
 * ================== */

async function deleteSubdistrict(id: string) {
  const response = await axiosInstance.delete(`/api/crud/subdistrict/${id}`);
  return response.data;
}

function useSubdistrictDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteSubdistrict(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subdistricts"],
      });
    },
  });
}

async function deleteBulkSubdistrict(ids: string[]) {
  return await Promise.all(ids.map((id) => deleteSubdistrict(id)));
}

function useSubdistrictBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) => deleteBulkSubdistrict(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["subdistricts"],
      });
    },
  });
}

async function pollMediaStatus(
  url: string,
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  const response: AxiosResponse = await axiosInstance.get(`${url}`);
  return response.data.data;
}

async function waitForMediaCompletion(
  url: string,
  interval = 2000,
  pollFn = pollMediaStatus, // Dependency injection for easier testing
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { status, path, originalFileName } = await pollFn(url);

    if (status === "SUCCESS" || status === "ERROR") {
      return { status, path, originalFileName: originalFileName };
    }

    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

async function exportSubDistrict(
  config: DataConfig = {},
  isDownload: string = "",
  onDownload: boolean = false,
  waitFn = waitForMediaCompletion, // Dependency injection for easier testing
  downloadFn = downloadFile, // Dependency injection for easier testing
): Promise<{ status: string; fileName?: string }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let createResponse: any = {};
  if (!isDownload) {
    createResponse = await axiosInstance.get("/api/crud/subdistrict/excel", {
      params: { ...config },
    });
    localStorage.setItem("isDownloadSubDistrict", new Date().toISOString());
  }

  const downloadParams = {
    className: "com.alurkerja.crud.masterdatasubdistrict.MasterDataSubdistrict",
    entityId: config.entityId,
  };
  const { status, path, originalFileName } = await waitFn(
    !isDownload
      ? `/api/crud/media/${createResponse.data.data.id}`
      : `/api/crud/media/last${generateParams(downloadParams)}`,
  );

  if (onDownload && status === "SUCCESS") {
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: { path, fileName: originalFileName, entityId: config.entityId },
        responseType: "blob",
      },
    );

    downloadFn(new Blob([downloadResponse.data]), originalFileName);
    localStorage.setItem("isDownloadSubDistrict", "");
    return { status: "IDLE" };
  } else {
    return { status };
  }
}

// Utility to handle file download
function downloadFile(
  blobData: Blob,
  fileName: string | undefined,
  createObjectURL = window.URL.createObjectURL,
): void {
  const url = createObjectURL(blobData);
  const link = document.createElement("a");
  link.href = url;
  if (typeof fileName === "string") {
    link.setAttribute("download", fileName);
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function useExportSubDistrict() {
  return useMutation({
    mutationFn: async ({
      config,
      isDownload,
      onDownload,
    }: {
      config?: DataConfig;
      isDownload?: string;
      onDownload?: boolean;
    }) => {
      return await exportSubDistrict(config, isDownload, onDownload);
    },
  });
}

export type { SubdistrictPayloadSchema, SubdistrictData };
export {
  subdistrictPayloadSchema,
  useSubdistricts,
  useSubdistrict,
  subdistrictLoader,
  useSubdistrictCreate,
  useSubdistrictEdit,
  useSubdistrictDelete,
  useSubdistrictBulkDelete,
  useExportSubDistrict,
  pollMediaStatus,
  waitForMediaCompletion,
  downloadFile,
  exportSubDistrict,
};
